// client/src/components/Verification.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import ApiService from '../services/api';

function Verification({ platform }) {
  const { account, isConnected, createPassportWithSignature, verifySocialAccountWithSignature } = useWeb3();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle'); 
  const navigate = useNavigate();

  useEffect(() => {
    if (!isConnected) {
      navigate('/');
      return;
    }

    async function checkVerificationStatus() {
      try {
        if (platform === 'twitter') {
          const status = await ApiService.getTwitterVerificationStatus();
          if (status.verified) {
            navigate('/verification/twitter/success');
          }
        } else if (platform === 'github') {
          const status = await ApiService.getGitHubVerificationStatus();
          if (status.verified) {
            navigate('/verification/github/success');
          }
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    }

    async function checkPassportStatus() {
      try {
        const { passport } = await ApiService.getPassportByAddress(account);
        
        if (passport && passport.verifications && passport.verifications[platform]) {
          navigate('/passport');
          return;
        }
      } catch (error) {
        console.error('Error checking passport status:', error);
      }
    }

    checkPassportStatus();
    checkVerificationStatus();
  }, [isConnected, navigate, platform, account]);

  const startVerification = async () => {
    if (!isConnected || !account) {
      setError('Please connect your wallet first');
      return;
    }

    setLoading(true);
    setError(null);
    setStatus('starting_verification');

    try {
      const { passport } = await ApiService.getPassportByAddress(account);
      
      if (!passport) {
        setStatus('creating_passport');
        const createResult = await createPassportWithSignature('General');
        
        if (!createResult.success) {
          throw new Error(createResult.error || 'Failed to create passport');
        }
        
        setStatus('starting_verification');
      }
      
      if (platform === 'twitter') {
        const result = await verifySocialAccountWithSignature('twitter');
        
        if (result.authUrl) {
          window.location.href = result.authUrl;
        } else {
          throw new Error('Failed to get Twitter authentication URL');
        }
      } else if (platform === 'github') {
        const result = await verifySocialAccountWithSignature('github');
        
        if (result.authUrl) {
          window.location.href = result.authUrl;
        } else {
          throw new Error('Failed to get GitHub authentication URL');
        }
      } else {
        throw new Error(`Verification for ${platform} is not yet implemented`);
      }
    } catch (err) {
      console.error('Verification error:', err);
      
      if (err.code === 4001) { 
        setError('You must sign the message to verify your account. This proves wallet ownership.');
      } else {
        setError(err.message || 'Failed to start verification');
      }
      
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const formatPlatformName = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  return (
    <div className="verification-container">
      <div className="hero-section">
        <h1 className="gradient-title">Account Verification</h1>
        <p className="subtitle">
          Connect your social accounts to your blockchain identity
        </p>
      </div>

      <div className="glass-card verification-card">
        <h2 className="card-title">
          Verify Your {formatPlatformName(platform)} Account
        </h2>

        {status === 'creating_passport' && (
          <div className="status-banner creating">
            <div className="loader"></div>
            <p>Creating your digital passport first...</p>
            <p className="status-info">
              This may take a few moments as we process your request on the blockchain.
            </p>
          </div>
        )}

        <div className="verification-info">
          <p className="info-title">
            Verifying your {formatPlatformName(platform)} account will:
          </p>
          <ul className="info-list">
            <li><span className="bullet">●</span> Create a new passport NFT if you don't have one</li>
            <li><span className="bullet">●</span> Add your {formatPlatformName(platform)} verification to your passport</li>
            <li><span className="bullet">●</span> Allow others to verify your on-chain identity</li>
          </ul>
          <p className="info-title">
            This process requires:
          </p>
          <ul className="info-list">
            <li><span className="bullet">●</span> Signing a message with your wallet to verify ownership</li>
            <li><span className="bullet">●</span> Authenticating with {formatPlatformName(platform)}</li>
            <li><span className="bullet">●</span> Confirming the verification on-chain</li>
          </ul>
        </div>

        {error && (
          <div className="alert-error">
            <p>{error}</p>
          </div>
        )}

        <div className="card-footer">
          <button
            onClick={startVerification}
            disabled={loading || !isConnected}
            className={`btn ${
              platform === 'twitter' 
                ? 'btn-twitter' 
                : platform === 'github'
                  ? 'btn-twitter'
                  : 'btn-gradient'
            } ${
              loading ? 'loading' : ''
            }`}
          >
            {loading ? (
              <>
                <span className="loader-sm"></span>
                <span>
                  {status === 'creating_passport' 
                    ? 'Creating Passport...' 
                    : 'Processing...'}
                </span>
              </>
            ) : (
              `Verify with ${formatPlatformName(platform)}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Verification;